import type { FieldDef } from '@packages/core';
import { PRECISION_CURRENCY, PRECISION_STANDARD, formatDecimalNatural, formatPercentageNatural } from '@packages/core';

const DECIMAL_PLACES_RE = /\.\d{0,(\d+)}/;

/** Matches in-progress decimal input (e.g. "", ".", "10.", "10.5"). */
export const PARTIAL_DECIMAL_INPUT_RE = /^(\d+\.?\d*|\.\d*)?$/;

export function isPartialDecimalInput(value: string): boolean {
  const cleaned = value.replace(/[$,%\s]/g, '').replace(/,/g, '');
  return PARTIAL_DECIMAL_INPUT_RE.test(cleaned);
}

export function cleanDecimalInput(value: string): string {
  return value.replace(/[$,%\s]/g, '').replace(/,/g, '');
}

/**
 * Returns true when the decimal portion of `value` exceeds `maxDecimals` places.
 * Used to reject keystrokes at the input boundary so the user cannot type
 * beyond the allowed precision.
 */
export function exceedsDecimalPrecision(value: string, maxDecimals: number): boolean {
  const dotIndex = value.indexOf('.');
  if (dotIndex === -1) return false;
  return value.length - dotIndex - 1 > maxDecimals;
}

/** Infer max decimal storage from domain JSON inputPrecision or Zod regex. */
export function inferStorageDecimalPlaces(
  def: Pick<FieldDef, 'schema' | 'displayPrecision' | 'displayFormat' | 'inputPrecision'> & { type?: string },
): number {
  if (def.inputPrecision != null) {
    return def.inputPrecision;
  }

  if (def.type === 'integer') {
    return 0;
  }

  const regexSource = extractRegexSource(def.schema);
  if (regexSource) {
    const match = regexSource.match(DECIMAL_PLACES_RE);
    if (match?.[1]) {
      return parseInt(match[1], 10);
    }
  }

  if (def.displayPrecision != null) {
    return def.displayPrecision;
  }

  if (def.displayFormat === 'currency') {
    return PRECISION_STANDARD;
  }

  return PRECISION_CURRENCY;
}

export function inferDisplayDecimalPlaces(def: Pick<FieldDef, 'displayPrecision' | 'displayFormat'>): number {
  if (def.displayPrecision != null) {
    return def.displayPrecision;
  }
  if (def.displayFormat === 'currency') {
    return PRECISION_CURRENCY;
  }
  return PRECISION_STANDARD;
}

function extractRegexSource(schema: FieldDef['schema']): string | null {
  const inner = schema as { _def?: { checks?: Array<{ kind?: string; regex?: RegExp }> } };
  const regexCheck = inner._def?.checks?.find((c) => c.kind === 'regex' && c.regex);
  return regexCheck?.regex?.source ?? null;
}

export function formatCanonicalForDisplay(
  value: unknown,
  displayPrecision: number,
  displayFormat?: FieldDef['displayFormat'],
): string {
  if (value == null || value === '') return '';
  const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  if (Number.isNaN(num)) return String(value);

  if (displayFormat === 'currency') {
    return `$${formatDecimalNatural(num, displayPrecision)}`;
  }
  if (displayFormat === 'percentage') {
    return formatPercentageNatural(num, displayPrecision);
  }
  return formatDecimalNatural(num, displayPrecision);
}

export function parseCanonicalNumeric(value: string, storagePrecision: number): number | '' {
  const cleaned = value.replace(/[$,%\s]/g, '').replace(/,/g, '');
  if (cleaned === '') return '';
  const num = Number(cleaned);
  if (Number.isNaN(num)) return '';
  const factor = 10 ** storagePrecision;
  return Math.round(num * factor) / factor;
}

export function normalizeCanonicalNumeric(value: unknown, storagePrecision: number): number | '' {
  if (value === '' || value == null) return '';
  if (typeof value === 'number') {
    const factor = 10 ** storagePrecision;
    return Math.round(value * factor) / factor;
  }
  return parseCanonicalNumeric(String(value), storagePrecision);
}

/**
 * Adds thousand separators to a partial decimal string while preserving
 * trailing dots and partial decimal portions.
 * Pure presentation — never mutates the canonical numeric value.
 */
export function formatLiveDraft(raw: string): string {
  if (raw === '' || raw === '.') return raw;

  const [intPart, ...rest] = raw.split('.');
  const hasDecimal = raw.includes('.');
  const decPart = rest.join('');

  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (hasDecimal) {
    return `${formattedInt}.${decPart}`;
  }
  return formattedInt;
}

/**
 * Computes the correct cursor position in `newFormatted` after live formatting
 * by counting how many significant characters (digits + dot) were before the
 * cursor in `oldValue`, then walking `newFormatted` to place the cursor after
 * the same count of significant characters.
 */
export function computeCursorAfterFormat(
  oldValue: string,
  newFormatted: string,
  oldCursor: number,
): number {
  let significantBefore = 0;
  for (let i = 0; i < oldCursor && i < oldValue.length; i++) {
    if (/[\d.]/.test(oldValue[i])) significantBefore++;
  }

  if (significantBefore === 0) return 0;

  let count = 0;
  for (let i = 0; i < newFormatted.length; i++) {
    if (/[\d.]/.test(newFormatted[i])) count++;
    if (count === significantBefore) return i + 1;
  }
  return newFormatted.length;
}
